/**
 * Mandarin Chinese text utilities for autocomplete
 * Provides pinyin to character conversion with translations
 */

export interface MandarinSuggestion {
  text: string; // Chinese characters
  pinyin: string; // Romanized pronunciation
  translation: string;
  type: 'exact' | 'partial' | 'variant';
}

/**
 * Common Mandarin words/phrases with pinyin and English translations
 * This is a starter set focused on HSK1-2 level vocabulary
 */
interface MandarinEntry {
  simplified: string;
  pinyin: string;
  translation: string;
}

const MANDARIN_DICTIONARY: MandarinEntry[] = [
  // Greetings
  { simplified: '你好', pinyin: 'nǐhǎo', translation: 'hello' },
  { simplified: '您好', pinyin: 'nínhǎo', translation: 'hello (formal)' },
  { simplified: '早上好', pinyin: 'zǎoshang hǎo', translation: 'good morning' },
  { simplified: '晚上好', pinyin: 'wǎnshang hǎo', translation: 'good evening' },
  { simplified: '再见', pinyin: 'zàijiàn', translation: 'goodbye' },
  { simplified: '谢谢', pinyin: 'xièxie', translation: 'thank you' },
  { simplified: '不客气', pinyin: 'bù kèqi', translation: 'you\'re welcome' },
  { simplified: '对不起', pinyin: 'duìbuqǐ', translation: 'sorry' },
  { simplified: '没关系', pinyin: 'méi guānxi', translation: 'it\'s okay' },

  // Common verbs
  { simplified: '是', pinyin: 'shì', translation: 'to be' },
  { simplified: '有', pinyin: 'yǒu', translation: 'to have' },
  { simplified: '在', pinyin: 'zài', translation: 'to be at/in' },
  { simplified: '来', pinyin: 'lái', translation: 'to come' },
  { simplified: '去', pinyin: 'qù', translation: 'to go' },
  { simplified: '吃', pinyin: 'chī', translation: 'to eat' },
  { simplified: '喝', pinyin: 'hē', translation: 'to drink' },
  { simplified: '看', pinyin: 'kàn', translation: 'to look/watch' },
  { simplified: '听', pinyin: 'tīng', translation: 'to listen' },
  { simplified: '说', pinyin: 'shuō', translation: 'to speak/say' },
  { simplified: '读', pinyin: 'dú', translation: 'to read' },
  { simplified: '写', pinyin: 'xiě', translation: 'to write' },
  { simplified: '学', pinyin: 'xué', translation: 'to study/learn' },
  { simplified: '做', pinyin: 'zuò', translation: 'to do/make' },
  { simplified: '坐', pinyin: 'zuò', translation: 'to sit' },
  { simplified: '站', pinyin: 'zhàn', translation: 'to stand' },
  { simplified: '走', pinyin: 'zǒu', translation: 'to walk' },
  { simplified: '跑', pinyin: 'pǎo', translation: 'to run' },
  { simplified: '买', pinyin: 'mǎi', translation: 'to buy' },
  { simplified: '卖', pinyin: 'mài', translation: 'to sell' },
  { simplified: '要', pinyin: 'yào', translation: 'to want' },
  { simplified: '想', pinyin: 'xiǎng', translation: 'to think/want' },
  { simplified: '知道', pinyin: 'zhīdào', translation: 'to know' },
  { simplified: '认识', pinyin: 'rènshi', translation: 'to know (person)' },
  { simplified: '会', pinyin: 'huì', translation: 'can/know how to' },
  { simplified: '能', pinyin: 'néng', translation: 'can/able to' },
  { simplified: '可以', pinyin: 'kěyǐ', translation: 'may/can' },
  { simplified: '工作', pinyin: 'gōngzuò', translation: 'to work/work' },
  { simplified: '学习', pinyin: 'xuéxí', translation: 'to study' },
  { simplified: '生活', pinyin: 'shēnghuó', translation: 'to live/life' },
  { simplified: '喜欢', pinyin: 'xǐhuan', translation: 'to like' },
  { simplified: '爱', pinyin: 'ài', translation: 'to love' },
  { simplified: '帮助', pinyin: 'bāngzhù', translation: 'to help' },
  { simplified: '问', pinyin: 'wèn', translation: 'to ask' },
  { simplified: '回答', pinyin: 'huídá', translation: 'to answer' },
  { simplified: '开始', pinyin: 'kāishǐ', translation: 'to begin' },
  { simplified: '结束', pinyin: 'jiéshù', translation: 'to end' },

  // Common nouns
  { simplified: '人', pinyin: 'rén', translation: 'person' },
  { simplified: '朋友', pinyin: 'péngyou', translation: 'friend' },
  { simplified: '老师', pinyin: 'lǎoshī', translation: 'teacher' },
  { simplified: '学生', pinyin: 'xuésheng', translation: 'student' },
  { simplified: '医生', pinyin: 'yīshēng', translation: 'doctor' },
  { simplified: '爸爸', pinyin: 'bàba', translation: 'dad' },
  { simplified: '妈妈', pinyin: 'māma', translation: 'mom' },
  { simplified: '儿子', pinyin: 'érzi', translation: 'son' },
  { simplified: '女儿', pinyin: 'nǚ\'er', translation: 'daughter' },
  { simplified: '哥哥', pinyin: 'gēge', translation: 'older brother' },
  { simplified: '姐姐', pinyin: 'jiějie', translation: 'older sister' },
  { simplified: '弟弟', pinyin: 'dìdi', translation: 'younger brother' },
  { simplified: '妹妹', pinyin: 'mèimei', translation: 'younger sister' },
  { simplified: '家', pinyin: 'jiā', translation: 'home/family' },
  { simplified: '学校', pinyin: 'xuéxiào', translation: 'school' },
  { simplified: '公司', pinyin: 'gōngsī', translation: 'company' },
  { simplified: '医院', pinyin: 'yīyuàn', translation: 'hospital' },
  { simplified: '饭店', pinyin: 'fàndiàn', translation: 'restaurant' },
  { simplified: '商店', pinyin: 'shāngdiàn', translation: 'store' },
  { simplified: '书', pinyin: 'shū', translation: 'book' },
  { simplified: '电脑', pinyin: 'diànnǎo', translation: 'computer' },
  { simplified: '手机', pinyin: 'shǒujī', translation: 'cellphone' },
  { simplified: '电视', pinyin: 'diànshì', translation: 'television' },
  { simplified: '电影', pinyin: 'diànyǐng', translation: 'movie' },
  { simplified: '音乐', pinyin: 'yīnyuè', translation: 'music' },
  { simplified: '水', pinyin: 'shuǐ', translation: 'water' },
  { simplified: '茶', pinyin: 'chá', translation: 'tea' },
  { simplified: '咖啡', pinyin: 'kāfēi', translation: 'coffee' },
  { simplified: '米饭', pinyin: 'mǐfàn', translation: 'rice' },
  { simplified: '面包', pinyin: 'miànbāo', translation: 'bread' },
  { simplified: '菜', pinyin: 'cài', translation: 'dish/vegetable' },
  { simplified: '水果', pinyin: 'shuǐguǒ', translation: 'fruit' },
  { simplified: '苹果', pinyin: 'píngguǒ', translation: 'apple' },
  { simplified: '钱', pinyin: 'qián', translation: 'money' },
  { simplified: '时间', pinyin: 'shíjiān', translation: 'time' },
  { simplified: '今天', pinyin: 'jīntiān', translation: 'today' },
  { simplified: '明天', pinyin: 'míngtiān', translation: 'tomorrow' },
  { simplified: '昨天', pinyin: 'zuótiān', translation: 'yesterday' },
  { simplified: '年', pinyin: 'nián', translation: 'year' },
  { simplified: '月', pinyin: 'yuè', translation: 'month' },
  { simplified: '天', pinyin: 'tiān', translation: 'day' },
  { simplified: '小时', pinyin: 'xiǎoshí', translation: 'hour' },
  { simplified: '分钟', pinyin: 'fēnzhōng', translation: 'minute' },
  { simplified: '中国', pinyin: 'zhōngguó', translation: 'China' },
  { simplified: '北京', pinyin: 'běijīng', translation: 'Beijing' },
  { simplified: '上海', pinyin: 'shànghǎi', translation: 'Shanghai' },

  // Common adjectives
  { simplified: '好', pinyin: 'hǎo', translation: 'good' },
  { simplified: '大', pinyin: 'dà', translation: 'big' },
  { simplified: '小', pinyin: 'xiǎo', translation: 'small' },
  { simplified: '多', pinyin: 'duō', translation: 'many/much' },
  { simplified: '少', pinyin: 'shǎo', translation: 'few/little' },
  { simplified: '高', pinyin: 'gāo', translation: 'tall/high' },
  { simplified: '矮', pinyin: 'ǎi', translation: 'short (height)' },
  { simplified: '长', pinyin: 'cháng', translation: 'long' },
  { simplified: '短', pinyin: 'duǎn', translation: 'short (length)' },
  { simplified: '新', pinyin: 'xīn', translation: 'new' },
  { simplified: '旧', pinyin: 'jiù', translation: 'old (things)' },
  { simplified: '快', pinyin: 'kuài', translation: 'fast' },
  { simplified: '慢', pinyin: 'màn', translation: 'slow' },
  { simplified: '热', pinyin: 'rè', translation: 'hot' },
  { simplified: '冷', pinyin: 'lěng', translation: 'cold' },
  { simplified: '贵', pinyin: 'guì', translation: 'expensive' },
  { simplified: '便宜', pinyin: 'piányi', translation: 'cheap' },
  { simplified: '漂亮', pinyin: 'piàoliang', translation: 'beautiful' },
  { simplified: '帅', pinyin: 'shuài', translation: 'handsome' },
  { simplified: '高兴', pinyin: 'gāoxìng', translation: 'happy' },

  // Numbers
  { simplified: '一', pinyin: 'yī', translation: 'one' },
  { simplified: '二', pinyin: 'èr', translation: 'two' },
  { simplified: '三', pinyin: 'sān', translation: 'three' },
  { simplified: '四', pinyin: 'sì', translation: 'four' },
  { simplified: '五', pinyin: 'wǔ', translation: 'five' },
  { simplified: '六', pinyin: 'liù', translation: 'six' },
  { simplified: '七', pinyin: 'qī', translation: 'seven' },
  { simplified: '八', pinyin: 'bā', translation: 'eight' },
  { simplified: '九', pinyin: 'jiǔ', translation: 'nine' },
  { simplified: '十', pinyin: 'shí', translation: 'ten' },

  // Common words
  { simplified: '的', pinyin: 'de', translation: '(possessive particle)' },
  { simplified: '了', pinyin: 'le', translation: '(aspect particle)' },
  { simplified: '吗', pinyin: 'ma', translation: '(question particle)' },
  { simplified: '呢', pinyin: 'ne', translation: '(question particle)' },
  { simplified: '不', pinyin: 'bù', translation: 'not' },
  { simplified: '没', pinyin: 'méi', translation: 'not/haven\'t' },
  { simplified: '很', pinyin: 'hěn', translation: 'very' },
  { simplified: '太', pinyin: 'tài', translation: 'too' },
  { simplified: '都', pinyin: 'dōu', translation: 'all/both' },
  { simplified: '也', pinyin: 'yě', translation: 'also' },
  { simplified: '还', pinyin: 'hái', translation: 'still/also' },
  { simplified: '就', pinyin: 'jiù', translation: 'then/just' },
  { simplified: '这', pinyin: 'zhè', translation: 'this' },
  { simplified: '那', pinyin: 'nà', translation: 'that' },
  { simplified: '哪', pinyin: 'nǎ', translation: 'which' },
  { simplified: '什么', pinyin: 'shénme', translation: 'what' },
  { simplified: '谁', pinyin: 'shéi', translation: 'who' },
  { simplified: '怎么', pinyin: 'zěnme', translation: 'how' },
  { simplified: '为什么', pinyin: 'wèishénme', translation: 'why' },
  { simplified: '哪里', pinyin: 'nǎlǐ', translation: 'where' },
  { simplified: '这里', pinyin: 'zhèlǐ', translation: 'here' },
  { simplified: '那里', pinyin: 'nàlǐ', translation: 'there' },
];

/**
 * Normalize pinyin by removing tone marks and converting to lowercase
 */
function normalizePinyin(pinyin: string): string {
  return pinyin
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics (tone marks)
    .replace(/[^a-z\s]/g, ''); // Keep only letters and spaces
}

/**
 * Generate Mandarin autocomplete suggestions based on pinyin input
 * @param input - User's pinyin input (with or without tone marks)
 * @returns Array of suggestions with Chinese characters and translations
 */
export function buildMandarinSuggestions(input: string): MandarinSuggestion[] {
  if (!input || input.trim() === '') return [];

  const trimmed = input.trim();
  const normalized = normalizePinyin(trimmed);
  const suggestions: MandarinSuggestion[] = [];
  const seen = new Set<string>();

  // Find matches
  MANDARIN_DICTIONARY.forEach((entry) => {
    if (seen.has(entry.simplified)) return;

    const entryPinyinNormalized = normalizePinyin(entry.pinyin);

    // Exact match (with or without tones)
    if (entryPinyinNormalized === normalized) {
      suggestions.push({
        text: entry.simplified,
        pinyin: entry.pinyin,
        translation: entry.translation,
        type: 'exact',
      });
      seen.add(entry.simplified);
      return;
    }

    // Partial match (word starts with input)
    if (entryPinyinNormalized.startsWith(normalized)) {
      suggestions.push({
        text: entry.simplified,
        pinyin: entry.pinyin,
        translation: entry.translation,
        type: 'partial',
      });
      seen.add(entry.simplified);
    }
  });

  // Limit to top 10 suggestions
  return suggestions.slice(0, 10);
}

/**
 * Normalize a Mandarin word for translation lookup
 */
export function normalizeMandarinWord(word: string): string {
  return word.trim();
}

/**
 * Check if text contains Chinese characters
 */
export function containsChinese(text: string): boolean {
  return /[\u4e00-\u9fa5]/.test(text);
}
